// Overlap detection: events overlap if they share ≥ OVERLAP_THRESHOLD minutes.
// Produces per-minute occupancy counts and the segments (start,end,count) where ≥2 events overlap.

import { MIN_PER_DAY } from "./time";

export const OVERLAP_THRESHOLD = 15;

// Returns array of overlap segments: { start, end, count, eventIds: [] }
// Only segments with count >= 2 are returned.
export function computeOverlapSegments(events) {
  if (!events || events.length < 2) return [];

  // For each pair, compute intersection and only count those overlapping >= threshold.
  // Then build coverage by minute using only "qualifying" overlap zones.
  const qualifyingPairs = [];
  for (let i = 0; i < events.length; i++) {
    for (let j = i + 1; j < events.length; j++) {
      const a = events[i];
      const b = events[j];
      // Parent/child relationship: a child event is intentionally "part of" its
      // parent, so an overlap between them is expected and never a conflict.
      if (a.parentEventId === b.id || b.parentEventId === a.id) continue;
      const start = Math.max(a.startMinutes, b.startMinutes);
      const end = Math.min(a.endMinutes, b.endMinutes);
      if (end - start >= OVERLAP_THRESHOLD) {
        qualifyingPairs.push({ start, end, ids: [a.id, b.id] });
      }
    }
  }
  if (!qualifyingPairs.length) return [];

  // Build minute occupancy from qualifying pair zones (counts distinct events present).
  const minute = new Array(MIN_PER_DAY).fill(null).map(() => new Set());
  for (const p of qualifyingPairs) {
    for (let m = p.start; m < p.end; m++) {
      minute[m].add(p.ids[0]);
      minute[m].add(p.ids[1]);
    }
  }

  // Compress consecutive minutes with same count into segments.
  const segments = [];
  let segStart = null;
  let segCount = 0;
  let segIds = new Set();

  for (let m = 0; m <= MIN_PER_DAY; m++) {
    const count = m < MIN_PER_DAY ? minute[m].size : 0;
    if (count >= 2) {
      if (segStart === null) {
        segStart = m;
        segCount = count;
        segIds = new Set(minute[m]);
      } else if (count !== segCount) {
        segments.push({ start: segStart, end: m, count: segCount, eventIds: [...segIds] });
        segStart = m;
        segCount = count;
        segIds = new Set(minute[m]);
      } else {
        for (const id of minute[m]) segIds.add(id);
      }
    } else if (segStart !== null) {
      segments.push({ start: segStart, end: m, count: segCount, eventIds: [...segIds] });
      segStart = null;
      segCount = 0;
      segIds = new Set();
    }
  }
  return segments;
}
